import * as XLSX from 'xlsx';
import moment from 'moment-timezone';

export const EXTERNAL_TRANSACTION_COLUMNS = [
  'Vehicle Number',
  'Quantity',
  'Date and Time'
];
export const MAX_EXTERNAL_TRANSACTION_UPLOAD_ROWS = 100;

const isBlank = (value) => value === null
  || value === undefined
  || (typeof value === 'string' && value.length === 0);

const getMomentValue = (value, timezoneName) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment.tz(value, timezoneName);
  }
  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) return null;
    return moment.tz({
      year: parsed.y,
      month: parsed.m - 1,
      day: parsed.d,
      hour: parsed.H,
      minute: parsed.M,
      second: parsed.S
    }, timezoneName);
  }
  if (typeof value === 'string') {
    const parsed = moment.tz(value, timezoneName);
    return parsed.isValid() ? parsed : null;
  }
  return null;
};

export const parseExternalTransactionWorkbook = async (file) => {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!worksheet) throw new Error('The workbook does not contain a worksheet.');

  const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', raw: true });
  const headers = data[0] || [];
  if (headers.length !== EXTERNAL_TRANSACTION_COLUMNS.length
    || headers.some((header, index) => header !== EXTERNAL_TRANSACTION_COLUMNS[index])) {
    throw new Error('The first row must contain exactly: Vehicle Number, Quantity, Date and Time.');
  }

  const rows = data.slice(1).reduce((result, values, index) => {
    if (values.every((value) => String(value ?? '').trim() === '')) return result;
    result.push({
      rowNumber: index + 2,
      values: {
        vehicleNumber: isBlank(values[0]) ? '' : String(values[0]),
        quantity: values[1],
        dateTime: values[2]
      }
    });
    return result;
  }, []);

  if (!rows.length) throw new Error('The workbook does not contain any transaction rows.');
  if (rows.length > MAX_EXTERNAL_TRANSACTION_UPLOAD_ROWS) {
    throw new Error(`You can upload a maximum of ${MAX_EXTERNAL_TRANSACTION_UPLOAD_ROWS} transactions at a time.`);
  }
  return rows;
};

export const validateExternalTransactionRows = (rows, timezoneName) => {
  const timezone = timezoneName || moment.tz.guess();
  const today = moment.tz(timezone).startOf('day');
  const yesterday = today.clone().subtract(1, 'day');

  return rows.map((row) => {
    const errors = [];
    const { vehicleNumber, quantity, dateTime } = row.values;
    const numericQuantity = Number(quantity);
    const parsedDateTime = getMomentValue(dateTime, timezone);

    if (!vehicleNumber) errors.push('Vehicle Number is required');
    if (!Number.isFinite(numericQuantity) || numericQuantity <= 0) {
      errors.push('Quantity must be greater than zero');
    }
    if (!parsedDateTime || !parsedDateTime.isValid()) {
      errors.push('Date and Time is required and must be valid');
    } else if (!parsedDateTime.isSame(today, 'day') && !parsedDateTime.isSame(yesterday, 'day')) {
      errors.push('Date and Time must be today or yesterday');
    }

    return {
      ...row,
      parsedDateTime,
      errors,
      status: errors.length ? 'invalid' : 'ready'
    };
  });
};

export const downloadExternalTransactionTemplate = () => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet([
    EXTERNAL_TRANSACTION_COLUMNS,
    ['KA01AB1234', 50, moment().format('YYYY-MM-DD HH:mm')]
  ]);
  worksheet['!cols'] = EXTERNAL_TRANSACTION_COLUMNS.map((header) => ({ wch: Math.max(header.length + 4, 20) }));
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');
  XLSX.writeFile(workbook, 'external-transaction-upload-template.xlsx');
};

export const formatExternalTransactionDateTime = (value, timezoneName) => {
  const parsed = getMomentValue(value, timezoneName || moment.tz.guess());
  return parsed?.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : String(value || '');
};
